import { orderingEvents, paymentEvents, useAnalytics } from '@order-up/analytics';
import { CartStorageService, createClientLogger, DeliveryAddressDialog, MenuThemeScope, formatDeliveryAddress, isASAPAvailable, isDeliveryAddressComplete, ScheduleModal, toScheduledFulfilment, useAuth, customerUidOf, useCapturePayment, useGetCartById, useGetOrderByCartId, useGetLocationBySlug, useLoyaltyBalance, useLoyaltyProgram, useOrderPreview, useOrderTimeAwareness, useProcessCheckout, useSavedCards, useTimeFormatter, useUpdateCartOrderConfig } from '@order-up/client/vite';
import { formatAmountForAPI, generateIdempotencyKey, getLocationDisplayName, roundToCents, translateError } from '@order-up/common';
import { IAuthResponse, ICaptureRequest, type IOrderDeliveryAddressComponents, type PaymentProviderName } from '@order-up/models';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BillingAddress, { type BillingAddressData } from '../components/BillingAddress';
import CustomerInformation, { type CustomerInfo } from '../components/CustomerInformation';
import LoyaltyRedeem from '../components/LoyaltyRedeem';
import OrderSummary from '../components/OrderSummary';
import PaymentInput, {
    isPaymentProvider,
    type ExtractedPayment,
    type PaymentInputHandle,
    type PaymentProvider,
    type TrxWalletPaymentResult,
} from '../components/PaymentInput';
import PickupDetails from '../components/PickupDetails';
import ScheduledCartNoticePanel from '../components/ScheduledCartNoticePanel';
import { scheduledCartNoticeFrom, type ScheduledCartNotice } from '../lib/scheduledCartNotice';
import { itemUnavailableNoticeFrom } from '../lib/itemUnavailableNotice';
import { useScheduledNoticeConfirm } from '../lib/useScheduledNoticeConfirm';
import PromoCode from '../components/PromoCode';
import GiftCardTender, { type AppliedGiftCard } from '../components/GiftCardTender';
import SavePaymentMethod from '../components/SavePaymentMethod';
import TipSelection from '../components/TipSelection';
import { useCheckoutTip } from '../lib/useCheckoutTip';
import { useMenuUrl } from '../lib/menuUrl';
import { captureCheckoutAttribution, clearCheckoutAttribution, readCheckoutAttribution } from '../lib/attribution';
import {
    checkoutSignature,
    isDefinitiveChargeFailure,
    resetIdempotencyKey as resetIdempotencyKeyPure,
    resolveIdempotencyKey as resolveIdempotencyKeyPure,
    type IdempotencyEntry,
} from '../lib/checkoutIdempotency';
import { resolveWalletOwner } from '../lib/walletOwner';
import { placedOrderForCart } from '../lib/placedCartOrder';
import { contactFieldsMessage, missingContactFields } from '../lib/customerContact';
import { MobileOrderSummarySkeleton } from '../components/skeletons/MobileOrderSummarySkeleton';
import { OrderSummarySkeleton } from '../components/skeletons/OrderSummarySkeleton';
import { PickupDetailsSkeleton } from '../components/skeletons/PickupDetailsSkeleton';

// Logger for checkout page
const logger = createClientLogger('CheckoutPage');

// ── Durable idempotency-key persistence ──────────────────────────────────────
// Parity with the POS PaymentIdempotencyService, which persists its key in Hive
// so an app crash/restart reuses the SAME key and can never double-charge. The
// web equivalent backs the in-memory ref with sessionStorage so a mid-payment
// page RELOAD (then resubmit) reuses the key the server already locked on —
// without this, a reload would lose the ref, mint a fresh key, and risk a
// duplicate charge for a request whose response was lost. sessionStorage scopes
// to the tab/session, which matches the lifetime of a single checkout hand-off.
// The pure helpers live in ../lib/checkoutIdempotency so they can be unit-tested
// without mounting React.

/**
 * Does a billing address satisfy the SAME rules the checkout API enforces
 * (address/city ≥1, region ≥2, postal ≥5 — a full US ZIP)? The ZIP input's
 * HTML5 `pattern` never fires because checkout submits via a button, not a
 * native <form>, so without gating on this a partial ZIP enables "Pay" and
 * the server rejects the whole request with an opaque 400
 * ("body/billingAddress/postal must NOT have fewer than 5 characters") — the
 * charge never even reaches the processor. Gate the button AND the submit
 * funnel on this so the user gets inline feedback instead of a 400.
 */
function billingMeetsApiRules(b: BillingAddressData | null): boolean {
    if (!b) return false;
    return (
        b.address.trim().length >= 1 &&
        b.city.trim().length >= 1 &&
        b.region.trim().length >= 2 &&
        b.postal.replace(/\D/g, '').length >= 5
    );
}

export default function CheckoutPage() {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const analytics = useAnalytics();

    // Extract data from URL query params
    const cartGuid = searchParams.get('cart');
    const sessionToken = searchParams.get('session'); // Session token from restaurant app for cross-domain auth

    const [selectedTip, setSelectedTip] = useState(20);
    const [customTip, setCustomTip] = useState('');
    // Default OFF, like both SMS boxes below. Two reasons, and the second is the
    // one that forced it: pre-ticked marketing consent is poor practice
    // everywhere and unlawful under opt-in regimes; and the 10DLC opt-in form is
    // submitted to carriers AS A SCREENSHOT, so a pre-filled box sitting between
    // the two SMS checkboxes is what a reviewer scanning for pre-filled consent
    // actually sees. Sent rejected this campaign once already (2026-09-09).
    const [promoEmail, setPromoEmail] = useState(false);
    const [promoText, setPromoText] = useState(false);
    // Transactional SMS opt-in (order confirmation + tracking link). Default off
    // — explicit opt-in keeps us on the right side of TCPA.
    const [smsOrderUpdates, setSmsOrderUpdates] = useState(false);
    // Special instructions for the whole order (allergies, "no onions", etc.).
    // Persisted end-to-end: schema → API → Order → shown on the POS order hub.
    const [customerNotes, setCustomerNotes] = useState('');
    // Customer Loyalty v1 (B8) — REDEMPTION INTENT: how many points the customer
    // chose to redeem. Integer only; the server derives the discount + burns
    // post-charge. Threaded into the preview (so the locked `expectedTotal`
    // reflects the discount) and the /checkout submit.
    const [redeemPoints, setRedeemPoints] = useState(0);
    // Coupon / promo-code REDEMPTION INTENT — the applied CODE (uppercase) the
    // customer entered. Threaded into the preview (so the displayed total AND the
    // locked `expectedTotal` reflect the server-derived coupon discount) and the
    // /checkout submit. The server derives + re-validates the discount; the UI
    // never sends a dollar amount. Empty until a code is applied.
    const [discountCode, setDiscountCode] = useState('');
    // Gift card tender (spec 2026-10-08 §4.2): the server-side HOLD the guest
    // applied — its id and the cents the SERVER reserved. The card is charged
    // only what is left; with the whole total held, no card is needed.
    const [giftCard, setGiftCard] = useState<AppliedGiftCard | null>(null);
    const [giftCardNotice, setGiftCardNotice] = useState<string | null>(null);
    // Customer Loyalty v1 (B8) — rewards opt-in for a NON-enrolled customer.
    // Default off (explicit consent). Sent as `loyaltyConsent` on /checkout.
    const [joinLoyalty, setJoinLoyalty] = useState(false);
    const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
        phone: '',
        firstName: '',
        lastName: '',
        email: '',
    });
    // Imperative ref to the PaymentInput wrapper — the card payload is
    // extracted at submit time via `ref.extract()`.
    const paymentRef = useRef<PaymentInputHandle>(null);
    // TRX-only readiness: true once the JS library's field iframes are
    // initialized (extract() will not throw not-ready). Gates the submit
    // button so a fast click during library init can't reach extract().
    const [trxFieldsReady, setTrxFieldsReady] = useState(false);
    const [billingAddress, setBillingAddress] = useState<BillingAddressData | null>(null);
    const [savePayment, setSavePayment] = useState(false);
    // $0 account-verification ("save card without purchase") is disabled until
    // the server checkout API supports it (see SavePaymentMethod wiring). Kept
    // as a hard-false const so the downstream guards remain a safety net.
    const accountVerificationMode = false;
    const [selectedProfile, setSelectedProfile] = useState<string | null>(null); // vault token id (TRX StorageSafe.Guid)
    // The processor that issued the selected saved card. A card reuses only
    // through its own processor's field, so an unknown one is never charged.
    const [selectedSavedProvider, setSelectedSavedProvider] = useState<PaymentProviderName | null>(null);
    const [selectedAccountGuid, setSelectedAccountGuid] = useState<string | null>(null); // TRX nested Account.Guid
    const [paymentError, setPaymentError] = useState<string | null>(null);
    // Title + next steps that accompany `paymentError`. Populated from the
    // server's cardholder-audience decline guidance (via `translateError`), so
    // a declined shopper is told what happened and what to do instead of
    // reading the gateway's own words.
    const [paymentErrorTitle, setPaymentErrorTitle] = useState<string | null>(null);
    const [paymentErrorSteps, setPaymentErrorSteps] = useState<string[]>([]);
    // Lines the server refused before charging (409 ITEM_UNAVAILABLE): sold out,
    // outside their menu's hours, or not offered online. Non-null = show the
    // "go back to the menu" notice INSTEAD of the payment-failed box — nothing
    // about the card was wrong.
    const [unavailableItems, setUnavailableItems] = useState<Array<{ name: string; reason: string }> | null>(null);
    // A scheduled-menu-pricing refusal at submit (a price moved with the clock,
    // or an item cannot be ordered now) — see `scheduledCartNotice.ts`.
    const [submitScheduledNotice, setSubmitScheduledNotice] = useState<ScheduledCartNotice | null>(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [lastPaymentResponse, setLastPaymentResponse] = useState<IAuthResponse | null>(null);

    // Feature flags
    const enableSavePayment = __VITE_ENV__.ENABLE_SAVE_PAYMENT === 'true';

    // Server-side checkout hook - handles order creation, payment, and cart cleanup atomically
    const checkoutMutation = useProcessCheckout();

    // Idempotency-key memo — the interactive-client half of the workspace
    // idempotency policy (see @order-up/common idempotency.ts). A payment key
    // is minted ONCE per intent via the shared generateIdempotencyKey() and
    // HELD across retries: if a charge succeeds but its response is lost and the
    // customer resubmits, the server's payment lock can only dedupe when the key
    // matches. The key is memoized per scope against a signature of the intent's
    // amount-defining inputs, so a genuinely changed order (different cart /
    // total / tip) mints a fresh key. resetIdempotencyKey() forces a fresh key
    // on the next submit and is called from the catch on a DEFINITIVE failure
    // (the charge did not land), so a customer can retry after a decline without
    // the server replaying the cached terminal `declined`.
    const idempotencyRefs = useRef<Record<string, IdempotencyEntry>>({});
    const resolveIdempotencyKey = (scope: string, signature: string, context?: string): string =>
        resolveIdempotencyKeyPure(
            idempotencyRefs.current,
            generateIdempotencyKey,
            scope,
            signature,
            context,
        );
    const resetIdempotencyKey = (scope: string): void =>
        resetIdempotencyKeyPure(idempotencyRefs.current, scope);

    // Keep specialized hooks for edge case features
    const captureMutation = useCapturePayment(); // For post-payment tip adjustment

    // Tip adjustment state (for post-payment tip capture)
    const [tipAdjustmentAmount, setTipAdjustmentAmount] = useState<string>('');
    const [tipAdjustmentSuccess, setTipAdjustmentSuccess] = useState(false);

    // TEST MODE: Override amount for processor validation testing (dev only - must edit code to enable)
    const [testAmount, setTestAmount] = useState<string>('');
    const showDebugUI = false; // Manually set to true in code to enable debug UI
    // `testAmount` is a string, so the `&&` chain would yield `string | boolean`
    // (the empty-string case). Coerce to a real boolean — `deriveTipAmount` and
    // the test-mode branches below all expect `boolean`.
    const isTestMode = Boolean(import.meta.env.DEV && testAmount && showDebugUI);

    // Get restaurant slug from URL path - same pattern as Next.js [slug]/page.tsx
    const locationSlug = slug || 'small-american-kitchen';
    // Load restaurant data using same hook as MenuPageClient
    const {
        data: locationResult,
        isLoading: isLocationLoading,
        error: locationError,
    } = useGetLocationBySlug(locationSlug);

    // The menu page the guest came from, including the location's own custom
    // domain — see lib/menuUrl.ts.
    const menuUrl = useMenuUrl(locationSlug, locationResult?.primaryDomain);

    // Ad attribution handed over in `#attr=` — read once on load, kept for the
    // tab, and taken out of the address bar (see lib/attribution.ts).
    useEffect(() => {
        captureCheckoutAttribution(locationSlug);
    }, [locationSlug]);

    const locationRecord = locationResult?.restaurant;
    const tenant = locationResult?.tenant;

    // Current checkout customer (anonymous or phone-linked session). Its UID is
    // the loyalty key: the server resolves the redeemable identity read-only by
    // this uid, and the self-access balance endpoint keys on it. Restaurant /
    // tenant doc IDs come from the resolved restaurant page (set by
    // getRestaurantBySlug).
    const { data: authData } = useAuth();
    const customerUid = customerUidOf(authData);
    const locationDocId = (locationRecord as { docId?: string } | undefined)?.docId;
    const tenantDocId = (tenant as { docId?: string } | undefined)?.docId;
    // Saving a card requires a verified phone (the server enforces this; the
    // Firebase user's `phoneNumber` mirrors the verified `phone_number` claim).
    // A guest / anonymous ordering session never sees the save option.
    const firebaseUser = authData?.firebaseUser as
        | { phoneNumber?: string | null; isAnonymous?: boolean }
        | undefined;
    const hasVerifiedPhone =
        !!firebaseUser && !firebaseUser.isAnonymous && !!firebaseUser.phoneNumber;
    // Saved cards for reuse — only for verified-phone accounts (the endpoint
    // is phone-gated server-side). Merchant-scoped, so keyed by restaurant.
    const savedCardsQuery = useSavedCards(
        hasVerifiedPhone ? tenantDocId : undefined,
        hasVerifiedPhone ? locationDocId : undefined,
    );
    const savedCards = savedCardsQuery.data ?? [];

    // Customer Loyalty v1 — ENROLLMENT OPT-IN surfacing. Earning is opt-in: a
    // customer must join before earning (the server enrolls only when we send
    // `enrollLoyalty`). We show the "Join rewards" checkbox when the loyalty
    // flag is on + the restaurant has an ACTIVE program + the customer isn't
    // already earning (no positive balance — an enrolled-with-points customer
    // sees the redeem control instead, not a join prompt). The server `enroll`
    // is idempotent, so a stray re-join is a harmless no-op.
    // `customerLoyalty` rides the location payload this page already loads
    // (`initiative.customer_loyalty` AND an active program, evaluated by the
    // API) — `/feature-flags/evaluate` refuses a guest session with 401.
    const loyaltyFlagEnabled = locationResult?.customerLoyalty === true;
    const { data: loyaltyProgram } = useLoyaltyProgram(
        loyaltyFlagEnabled ? tenantDocId : undefined,
        loyaltyFlagEnabled ? locationDocId : undefined,
    );
    const { data: loyaltyBalanceData } = useLoyaltyBalance(
        loyaltyFlagEnabled ? tenantDocId : undefined,
        loyaltyFlagEnabled ? locationDocId : undefined,
        loyaltyFlagEnabled ? customerUid : undefined,
    );
    const alreadyEarning = (loyaltyBalanceData?.balance ?? 0) > 0;
    const showLoyaltyOptIn =
        loyaltyFlagEnabled && loyaltyProgram?.status === 'active' && !alreadyEarning;

    // Public apps have no SelectionProvider, so pass the resolved restaurant's
    // settings directly to get accurate per-restaurant clock/date formatting.
    const { formatTime } = useTimeFormatter(locationRecord?.settings);

    // Resolve the active payment provider STRICTLY from the restaurant's
    // `providerRouting.defaultProvider`. NO fallback: when a restaurant hasn't
    // configured a processor — or names one this build has no card form for —
    // this is null and checkout is blocked (fail loud); we never guess a
    // processor. The server also fails closed, so a guessed provider would be
    // rejected anyway. `null` while the restaurant is
    // still loading is fine — the submit gate + payment section handle it.
    // The name a DINER should see. `restaurant.name` is the legal entity
    // ("<Brand> LLC"); `displayName` is the trading name. Checkout printed the
    // legal one in the tab title, the summary and the consent copy, so the
    // brand changed between the menu and the payment screen.
    const locationDisplayName = getLocationDisplayName(locationRecord);

    const routedProvider: unknown = locationRecord?.providerRouting?.defaultProvider;
    const paymentProvider: PaymentProvider | null = isPaymentProvider(routedProvider)
        ? routedProvider
        : null;

    // Load cart from Firestore using cart GUID from URL
    const {
        data: cart,
        isLoading: isCartLoading,
        isFetched: isCartFetched,
        error: cartError,
    } = useGetCartById(cartGuid);

    // Check for existing order for this cart (prevents duplicates on retry).
    // C9: scope is the numeric tenant/restaurant business IDs (the API resolves
    // doc IDs server-side); blanket Firestore order listing is removed.
    const { data: existingOrder } = useGetOrderByCartId(
        cart?.cartId || null,
        tenant?.tenantId || 0,
        locationRecord?.restaurantId || 0
    );

    // THE tip — one derivation, one base (server subtotal once previewed),
    // shared by the tiles and the charge. See `useCheckoutTip`.
    const {
        tipAmount: initialTipAmount,
        tipCentsFor,
        notePreviewSubtotal,
    } = useCheckoutTip({ isTestMode, cartSubtotal: cart?.subtotal, selectedTip, customTip });

    // Get server-side preview with EXACT totals (single source of truth)
    // This prevents any price discrepancies between display and actual charge
    const {
        data: preview,
        isLoading: previewLoading,
        // True while a tip/redeem change is re-previewing and the summary is
        // showing the PREVIOUS preview's totals. We block submit in this window
        // so the amount-lock can't fire against a stale total.
        isPlaceholderData: isPreviewStale,
        error: previewError,
        refetch: refetchPreview
    } = useOrderPreview(
        cart?.cartId || null,
        initialTipAmount,
        // Loyalty redemption intent (B8). Re-previewing with this keeps the
        // displayed total AND the locked `expectedTotal` consistent with the
        // server-derived `loyaltyDiscount`, so a redeem change never triggers a
        // PRICE_CHANGED surprise at submit.
        redeemPoints,
        // Coupon redemption intent. Same contract as `redeemPoints`: a code change
        // re-previews so `expectedTotal` stays consistent with the server-derived
        // `couponDiscount` (or surfaces `couponError` for a rejected code).
        discountCode || undefined
    );

    // Feed the server-priced subtotal back into the tip base (see useCheckoutTip).
    useEffect(() => {
        notePreviewSubtotal(preview?.subtotal);
    }, [preview?.subtotal, notePreviewSubtotal]);

    // Apply restaurant branding via CSS custom properties
    useEffect(() => {
        if (locationRecord?.websiteConfig?.branding) {
            const { colors } = locationRecord.websiteConfig.branding;

            document.documentElement.style.setProperty('--brand-primary', colors.primary);
            document.documentElement.style.setProperty('--brand-secondary', colors.secondary);
            document.documentElement.style.setProperty('--brand-accent', colors.accent);

            document.title = `Checkout - ${getLocationDisplayName(locationRecord)} | OrderUp`;
        }
    }, [locationRecord]);

    // Log cart session info for debugging
    // Note: Cross-app auth is now handled by CustomerAuthHandler in ClientProviders
    useEffect(() => {
        if (cart) {
            logger.debug('Cart loaded', {
                cartId: cart.cartId,
                cartGuid: cart.guid,
                customerId: cart.customerId,
                hasSessionToken: !!sessionToken
            });
        }
    }, [cart, sessionToken]);

    // ── Analytics funnel guards ──────────────────────────────────────────────
    // Fire-once latches so re-renders / preview refetches don't re-emit funnel
    // events. The provider is error-swallowing + dev-mocked (BaseAnalyticsProvider),
    // so these calls are safe even when analytics is a no-op.
    const checkoutStartedSent = useRef(false);
    const paymentMethodSelectedSent = useRef(false);

    // checkout_started — fire once when the cart + restaurant have resolved and
    // the checkout page is actually rendering its form (the funnel entry point).
    useEffect(() => {
        if (checkoutStartedSent.current) return;
        if (!cart || !locationRecord) return;
        checkoutStartedSent.current = true;
        analytics.trackEvent(orderingEvents.checkoutStarted({
            cartId: String(cart.cartId),
            restaurantId: String(locationRecord.restaurantId),
            itemCount: cart.items?.length ?? 0,
            subtotal: cart.subtotal ?? 0,
            orderType: cart.orderType ?? 'pickup',
        }));
    }, [cart, locationRecord, analytics]);

    // checkout_step_completed (payment_method) — fire once the customer has a
    // usable payment method: a ready card form, or a selected saved card. This is the real "payment method selected" outcome.
    useEffect(() => {
        if (paymentMethodSelectedSent.current) return;
        if (!cart || !locationRecord) return;
        const hasMethod = !!selectedProfile || (paymentProvider === 'trx' && trxFieldsReady);
        if (!hasMethod) return;
        paymentMethodSelectedSent.current = true;
        analytics.trackEvent(orderingEvents.checkoutStepCompleted({
            cartId: String(cart.cartId),
            restaurantId: String(locationRecord.restaurantId),
            step: 'payment_method',
            stepDurationSeconds: 0,
        }));
    }, [cart, locationRecord, selectedProfile, paymentProvider, trxFieldsReady, analytics]);

    // Get restaurant address for display - same pattern as MenuPageClient
    const fullAddress = useMemo(() => {
        if (!locationRecord?.address) return '';
        const { street, city, state } = locationRecord.address;
        return `${street}, ${city}, ${state}`;
    }, [locationRecord]);

    // Read order configuration from cart (saved by ordering app)
    // This ensures checkout uses the EXACT same config the user selected in ordering
    const orderConfig = useMemo(() => {
        if (!cart) return null;

        return {
            orderType: cart.orderType,
            deliveryAddress: cart.deliveryAddress,
            scheduledTime: cart.scheduledTime,
            isConfigured: true
        };
    }, [cart]);

    // The location shown in "Pickup/Delivery details": for delivery, the
    // CUSTOMER's address (where the order is going); for pickup, the restaurant.
    const displayLocationAddress = useMemo(() => {
        if (orderConfig?.orderType === 'delivery') {
            return orderConfig.deliveryAddress || fullAddress;
        }
        return fullAddress;
    }, [orderConfig, fullAddress]);

    // Live time-awareness — the same evaluator the ordering app uses, ticking
    // every 30s. ASAP re-derives its ready estimate (now + configured prep time)
    // against the real clock, so the shown estimate can NEVER go stale; a
    // scheduled slot that passes (or drops under the minimum lead, or the
    // kitchen closing) flags invalid and blocks submit until corrected — which
    // the customer can now do IN checkout via the fulfillment controls below.
    const timeState = useOrderTimeAwareness(locationRecord, cart?.scheduledTime, locationRecord?.settings);
    // The clock the ASAP prep-quote label is read against. Held in state and
    // ticked every 30s (the same cadence the time-awareness hook re-derives on),
    // because reading Date.now() during render is impure: two renders of the
    // same state could print two different times.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);
    const timeBlocksCheckout = !!cart && !timeState.isValid;

    // ── Order-config parity (full control of time + order type in checkout) ──
    // Mirrors the ordering app's fulfillment controls: pickup/delivery switch
    // (courier-gated), schedule/ASAP picker, delivery-address editor. Changes
    // persist to the Firestore cart (the server's source of truth at /checkout)
    // and invalidate the order preview so delivery fees / totals recompute.
    const cartConfigMutation = useUpdateCartOrderConfig();
    // Scheduled menu pricing: the guest accepts the server's re-pricing here.
    // The cart now holds the server's answer; the preview re-quotes it and the
    // guest places the order again.
    const scheduledConfirm = useScheduledNoticeConfirm({
        onApplied: () => setSubmitScheduledNotice(null),
        onError: (error) => {
            setPaymentErrorTitle("We couldn't update your cart");
            setPaymentErrorSteps([]);
            setPaymentError(translateError(error).message);
        },
    });
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [isAddressOpen, setIsAddressOpen] = useState(false);

    // Delivery is offered only when a courier provider is configured (matches
    // the server dispatch gate + the ordering app's CartModal/OrderModal).
    const deliveryOffered = useMemo(() => {
        const cp = locationRecord?.settings?.courierProvider;
        return !!cp && cp !== 'none';
    }, [locationRecord]);

    // Recomputed each render (the 30s tick re-renders) so the "Switch to ASAP"
    // recovery action tracks the restaurant's live open/close state.
    const asapAvailable = isASAPAvailable(locationRecord);

    const updateCartConfig = (config: {
        orderType?: 'pickup' | 'delivery';
        scheduledTime?: Date;
        deliveryAddress?: string;
        deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    }) => {
        if (!cart || !tenant || !locationRecord) return;
        cartConfigMutation.mutate(
            {
                cart,
                tenantId: tenant.tenantId,
                restaurantId: locationRecord.restaurantId,
                config,
            },
            {
                onError: (error) => {
                    logger.error('Failed to update order config', error);
                    setPaymentError("We couldn't update your order settings. Please try again.");
                },
            },
        );
    };

    const handleSelectPickup = () => {
        if (cart && cart.orderType !== 'pickup') {
            updateCartConfig({ orderType: 'pickup' });
        }
    };

    const handleSelectDelivery = () => {
        if (!cart || cart.orderType === 'delivery') return;
        const existing = cart.deliveryAddressComponents;
        if (isDeliveryAddressComplete(existing)) {
            updateCartConfig({
                orderType: 'delivery',
                deliveryAddress: formatDeliveryAddress(existing),
                deliveryAddressComponents: existing,
            });
        } else {
            // No saved address yet — capture it first; the save persists the
            // orderType switch together with the address.
            setIsAddressOpen(true);
        }
    };

    const handleSaveAddress = (components: IOrderDeliveryAddressComponents, formatted: string) => {
        updateCartConfig({
            orderType: 'delivery',
            deliveryAddress: formatted,
            deliveryAddressComponents: components,
        });
        setIsAddressOpen(false);
    };

    // undefined = ASAP. The Firestore sync DELETES the old slot (not merely
    // omits it), so a passed scheduled time is genuinely recoverable here.
    const handleScheduleConfirm = (date: Date | undefined) => {
        updateCartConfig({ scheduledTime: date });
    };

    const handleSwitchToAsap = () => {
        updateCartConfig({ scheduledTime: undefined });
    };

    // Format scheduled time for display
    const deliveryTimeDisplay = useMemo(() => {
        // Check if we have a scheduled time
        if (!orderConfig?.scheduledTime) {
            // ASAP: for delivery, surface the live Drive ETA when available;
            // otherwise the live evaluator label ("ASAP · ready ~7:45 PM"),
            // re-derived every 30s as now + the prep time configured in the
            // management app — never a stale materialized timestamp.
            if (orderConfig?.orderType === 'delivery' && preview?.etaMinutes) {
                return `ASAP · ~${preview.etaMinutes} min`;
            }
            // The server's prep quote — the time this order will actually be
            // promised for, which is longer than the configured prep time when
            // the kitchen is busy (dynamic prep time). The client estimate is
            // only the fallback until the preview arrives.
            if (timeState.status === 'asap' && preview?.prepTimeMinutes) {
                const ready = new Date(nowMs + preview.prepTimeMinutes * 60_000);
                return `ASAP · ready ~${formatTime(ready)}`;
            }
            return timeState.label;
        }

        try {
            const scheduledDate = new Date(orderConfig.scheduledTime);
            const now = new Date();
            const isToday = scheduledDate.toDateString() === now.toDateString();
            const isTomorrow = scheduledDate.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();

            const timeString = formatTime(scheduledDate);

            if (isToday) {
                return `Today at ${timeString}`;
            } else if (isTomorrow) {
                return `Tomorrow at ${timeString}`;
            } else {
                const dateString = scheduledDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
                return `${dateString} at ${timeString}`;
            }
        } catch (error) {
            logger.error('Error formatting scheduled time', error);
            return 'As soon as possible';
        }
    }, [orderConfig, preview, formatTime, timeState, nowMs]);

    // Shared props for the two PickupDetails instances (mobile stack + desktop
    // section) so the fulfillment controls can never drift between layouts.
    const pickupDetailsProps = {
        address: displayLocationAddress,
        orderType: (cart?.orderType || 'pickup') as 'pickup' | 'delivery',
        deliveryTime: deliveryTimeDisplay,
        deliveryOffered,
        updating: cartConfigMutation.isPending,
        timeWarning: timeBlocksCheckout ? timeState.warning : undefined,
        asapAvailable,
        onSelectPickup: handleSelectPickup,
        onSelectDelivery: handleSelectDelivery,
        onChangeTime: () => setIsScheduleOpen(true),
        onSwitchToAsap: handleSwitchToAsap,
        onEditAddress: () => setIsAddressOpen(true),
    };

    // Use the calculated tip amount for display
    // This value is passed to the preview hook above, which will recalculate when it changes
    const tipAmount = initialTipAmount;

    // Use server-calculated totals (single source of truth)
    // TEST MODE: Use testAmount if provided (for processor validation testing)
    //
    // Subtotal + the per-item line prices come straight from the cart, which
    // loads fast from Firestore — so they paint immediately and the customer
    // never sees a zeroed summary. Tax, fees and the grand total are
    // SERVER-derived in the order preview (the authoritative amount-lock source);
    // while that ~300ms call is in flight we mark them `totalsPending` so the
    // summary renders a skeleton on just those lines instead of a broken "$0.00"
    // flash. The preview's own subtotal supersedes the cart's once it arrives.
    const totalsPending = !isTestMode && !preview && !previewError;
    // The preview's own scheduled-pricing refusal, if that is why it failed.
    const previewScheduledNotice = useMemo(() => scheduledCartNoticeFrom(previewError), [previewError]);
    // The preview's own "these lines cannot be ordered" refusal — the 409
    // ITEM_UNAVAILABLE /checkout answers pre-charge, answered at review too.
    const previewUnavailable = useMemo(() => itemUnavailableNoticeFrom(previewError), [previewError]);
    // What the unavailable-items panel shows: the submit's refusal, else the
    // preview's.
    const shownUnavailableItems = unavailableItems ?? previewUnavailable?.items ?? null;
    const shownUnavailableMessage = unavailableItems ? paymentError : previewUnavailable?.message ?? null;
    const scheduledCartNotice = submitScheduledNotice ?? previewScheduledNotice;
    const subtotal = isTestMode ? parseFloat(testAmount) : (preview?.subtotal ?? cart?.subtotal ?? 0);
    const onlineServiceFee = isTestMode ? null : (preview?.onlineServiceFee ?? null);
    // The fee's TAX joins the item tax; the fee itself has its own line.
    // Rounded like every other surface (cart, receipt): two dollar figures
    // summed in floating point format a cent low with toFixed.
    const taxAndFees = isTestMode ? 0 : roundToCents((preview?.tax || 0) + (onlineServiceFee?.tax ?? 0));
    const deliveryFee = isTestMode ? 0 : (preview?.deliveryFee || 0);
    const total = isTestMode ? parseFloat(testAmount) : (preview?.total || 0);

    // Place-order CTA label. Until the server preview returns the authoritative
    // total we show a bare "Place order" rather than "Place order • $0.00" — the
    // priced label only appears once we have a real total to charge. Uses
    // `total` (the amount actually charged).
    // With a gift card applied, the card is charged only the remainder — the
    // label shows what the CARD pays (or that nothing is charged to one).
    const totalCents = Math.round(total * 100);
    const giftCents = giftCard ? Math.min(giftCard.amountCents, totalCents) : 0;
    const cardDueCents = Math.max(0, totalCents - giftCents);
    const giftCoversAll = giftCard !== null && !totalsPending && cardDueCents === 0;
    const placeOrderLabel = totalsPending
        ? 'Place order'
        : giftCoversAll
            ? 'Place order • paid by gift card'
            : `Place order • $${(cardDueCents / 100).toFixed(2)}`;

    // Validate form is complete before allowing payment
    // Required contact details still missing from the form (lib/customerContact).
    const missingContact = useMemo(() => missingContactFields(customerInfo), [customerInfo]);

    const isFormValid = useMemo(() => {
        // No configured payment provider → cannot submit (fail closed; never
        // guess one). The payment section shows an "unavailable" notice.
        if (!paymentProvider) return false;

        // Every path, saved cards included: phone, name and the receipt email.
        if (missingContact.length > 0) return false;

        // Scenario 3: reusing a saved card. TRX StorageSafe reuse needs no
        // CVV — the StorageSafe.Guid is sufficient, so the order is ready as
        // soon as a card is selected. A card from any other processor is
        // never ready (fail closed).
        if (selectedProfile) return selectedSavedProvider === 'trx';

        // New card: extract is submit-time. Gate on the library's readiness
        // signal, then validate the surrounding fields and trust the
        // imperative extract on submit to catch invalid card data with a
        // user-readable error.
        if (!trxFieldsReady) return false;
        // Full billing (incl. a 5-digit ZIP) — matches the API so a short
        // ZIP disables Pay rather than producing a submit-time 400.
        return billingMeetsApiRules(billingAddress);
    }, [selectedProfile, selectedSavedProvider, billingAddress, missingContact, paymentProvider, trxFieldsReady]);

    // Process payment using robust server-side checkout API. `walletOverride`
    // is set ONLY when triggered by an Apple/Google Pay sheet completion — it
    // carries the wallet payload and skips the card-extract step.
    const handlePayment = async (walletOverride?: TrxWalletPaymentResult) => {
        // A wallet sheet has ALREADY collected the payer's name, email and
        // billing address — asking them to retype it is pure friction, and the
        // address is what earns AVS + the Level-2 tier at the processor.
        //
        // Derived, not setState: React state updates are async, so anything set
        // here would still be stale for THIS invocation — and the wallet path
        // submits immediately (~1-min cryptogram TTL), so there is no later
        // render to wait for. The form state is synced afterwards, purely so
        // the UI reflects what was used.
        const walletContact = walletOverride?.contact;
        const effectiveCustomer: CustomerInfo = walletContact
            ? {
                  firstName: walletContact.firstName ?? customerInfo.firstName,
                  lastName: walletContact.lastName ?? customerInfo.lastName,
                  email: walletContact.email ?? customerInfo.email,
                  phone: walletContact.phone ?? customerInfo.phone,
              }
            : customerInfo;
        const effectiveBilling: BillingAddressData | null = walletContact?.billing
            ? {
                  name: `${effectiveCustomer.firstName} ${effectiveCustomer.lastName}`.trim(),
                  ...(effectiveCustomer.email ? { email: effectiveCustomer.email } : {}),
                  ...(effectiveCustomer.phone ? { phone: effectiveCustomer.phone } : {}),
                  address: walletContact.billing.address,
                  ...(walletContact.billing.address2
                      ? { address2: walletContact.billing.address2 }
                      : {}),
                  city: walletContact.billing.city,
                  region: walletContact.billing.region,
                  postal: walletContact.billing.postal,
                  country: walletContact.billing.country,
              }
            : billingAddress;

        // Early validation
        if (!cart || !locationRecord || !tenant) {
            logger.error('Missing required data');
            setPaymentError('Missing required restaurant or cart data. Please try again.');
            return;
        }

        // Check for an already placed (so already paid) order. Send the
        // customer TO the order rather than leaving them on a dead-end error:
        // this cart is spent, and the useful answer is the order it became.
        // (Prod 2026-07-30 showed the opposite failure — an already-paid cart
        // that stayed checkout-able.) Falls back to the error message when the
        // order carries no guid to navigate to, so this can never strand the
        // customer on a blank page.
        const placedOrder = placedOrderForCart(existingOrder);
        if (placedOrder) {
            logger.warn('Cart already has a paid order', {
                orderId: placedOrder.orderId,
                orderGuid: placedOrder.orderGuid,
            });
            if (placedOrder.orderGuid) {
                navigate(`/${locationSlug}/confirmation/${placedOrder.orderGuid}`);
                return;
            }
            setPaymentError('This order has already been paid.');
            return;
        }

        // Account verification mode ($0 auth) not yet supported by API - keep client-side
        // TODO: Add account verification mode support to server-side checkout API
        if (accountVerificationMode) {
            logger.warn('Account verification mode not yet supported by server API');
            setPaymentError('Saving payment method without purchase is not yet available. Please make a purchase to save your card.');
            return;
        }

        // Validate payment method. selectedProfile is the saved-card path; a new
        // card is extracted at submit time — we just check the wrapper ref is
        // mounted here and let the actual extract fail loudly with a
        // user-readable error if the iframes aren't ready / fields are invalid.
        const hasPaymentMethod =
            giftCoversAll || walletOverride || selectedProfile || (paymentProvider === 'trx' && paymentRef.current);
        if (!hasPaymentMethod) {
            logger.error('Missing payment method');
            setPaymentError('Please enter your payment information.');
            return;
        }

        // Validate customer/billing info (required for new cards + wallets,
        // optional for saved profiles). This is the single funnel for card AND
        // wallet (Apple/Google Pay) submits, so enforcing the API's billing
        // rules here — a full ZIP included — stops a short/partial ZIP from
        // reaching the server as an opaque 400 on either path.
        // Validated against the EFFECTIVE values, so a wallet payer satisfies
        // this with what Apple/Google already collected instead of being
        // blocked on empty form fields (the "Missing/invalid required
        // billing/customer data" dead end seen in prod 2026-07-30).
        // Contact first, and on EVERY path — saved cards included: email is
        // the receipt channel (see lib/customerContact.ts), and a wallet has
        // already been authorized by the time this runs, so the message has
        // to say exactly what is missing. Checked against the EFFECTIVE values,
        // so what an owned wallet sheet collected counts.
        const missingAtSubmit = missingContactFields(effectiveCustomer);
        if (missingAtSubmit.length > 0) {
            logger.warn('Checkout blocked on missing contact details', {
                missing: missingAtSubmit,
                viaWallet: !!walletOverride,
            });
            setPaymentError(contactFieldsMessage(missingAtSubmit));
            return;
        }
        if (!selectedProfile && !billingMeetsApiRules(effectiveBilling)) {
            logger.error('Missing/invalid required billing/customer data', {
                viaWallet: !!walletContact,
                hasBilling: !!effectiveBilling,
            });
            setPaymentError(
                effectiveBilling && effectiveBilling.postal.replace(/\D/g, '').length < 5
                    ? 'Please enter a valid 5-digit ZIP code.'
                    : 'Please complete all required fields.',
            );
            return;
        }

        // Reflect what the wallet supplied back into the form, so the summary
        // and any later card retry show the same details that were charged.
        if (walletContact) {
            setCustomerInfo(effectiveCustomer);
            if (effectiveBilling) setBillingAddress(effectiveBilling);
        }

        setPaymentError(null);
        setUnavailableItems(null);

        try {
            // Validate preview is loaded (needed for the amount-lock AND the
            // idempotency intent signature below).
            if (!preview) {
                setPaymentError('Unable to calculate order total. Please refresh and try again.');
                return;
            }

            // Guard against charging with a STALE total: when the tip/redemption
            // just changed, `preview` is still the previous entry's value while
            // the recalculated one is in flight. Submitting here would lock
            // `expectedTotal` to a total that doesn't match the tip we send, so we
            // wait for the fresh preview rather than risk a PRICE_CHANGED reject.
            if (isPreviewStale) {
                await refetchPreview();
                return;
            }

            // Idempotency key — minted once per payment intent and reused across
            // retries, keyed to the order's amount-defining inputs. Prevents a
            // double-charge on a lost-response resubmit; rotates on a definitive
            // decline (see catch) so the customer can retry with a fixed card.
            // The gift split is amount-defining too: a different gift leg is a
            // different card charge, so it must mint a different key.
            const idempotencyKey = resolveIdempotencyKey(
                'checkout',
                `${checkoutSignature(cart.cartId, preview.total, tipAmount)}:gift${giftCents}`,
                String(cart.cartId),
            );

            const attribution = readCheckoutAttribution(locationSlug);

            logger.info('Processing server-side checkout', {
                cartId: cart.cartId,
                idempotencyKey,
                tipAmount,
                hasProfile: !!selectedProfile,
                savePayment: savePayment && enableSavePayment,
            });

            // payment_initiated — the customer submitted payment. `method` maps
            // our online card-not-present path; saved-profile is still CNP.
            // `orderId` isn't minted until the server creates the order, so the
            // cartId is the stable correlation id at submit time.
            const paymentStartedAt = Date.now();
            analytics.trackEvent(paymentEvents.paymentInitiated({
                tenantId: String(tenant.tenantId),
                restaurantId: String(locationRecord.restaurantId),
                orderId: String(cart.cartId),
                amount: preview.total,
                method: 'card_not_present',
                processor: paymentProvider ?? 'unknown',
            }));

            // For TRX: extract the encrypted card data NOW (submit time).
            // The library deactivates fields after extract — must happen
            // immediately before the API call, not earlier. The 15-min
            // encrypted-data TTL begins now and is honored by the
            // network round-trip taking <1s in practice.
            let extracted: ExtractedPayment | null = null;
            if (paymentProvider === 'trx' && !selectedProfile && !walletOverride && !giftCoversAll) {
                if (!paymentRef.current) {
                    setPaymentError('Payment form not ready. Please wait a moment and try again.');
                    return;
                }
                try {
                    extracted = await paymentRef.current.extract();
                } catch (err) {
                    const msg = err instanceof Error ? err.message : 'Could not read card details. Please try again.';
                    logger.warn('TRX extract failed', { error: msg });
                    setPaymentError(msg);
                    paymentRef.current.enableFields();
                    return;
                }
            }

            // Build checkout request for server-side processing
            // Server handles: cart validation, order creation, payment, and cart cleanup atomically
            const response = await checkoutMutation.mutateAsync({
                request: {
                    // Cart identification
                    cartId: cart.cartId,

                    // Customer information
                    // EFFECTIVE — a wallet payer's name/email/phone come from
                    // the sheet, so the order and its receipt carry the details
                    // the customer actually confirmed rather than blanks.
                    customerInfo: {
                        firstName: effectiveCustomer.firstName,
                        lastName: effectiveCustomer.lastName,
                        // Guaranteed present — the contact check above refuses without it.
                        email: effectiveCustomer.email.trim(),
                        phone: effectiveCustomer.phone,
                    },

                    // Payment information — exactly one of:
                    //   * walletPayment            (Apple/Google Pay sheet)
                    //   * encryptedPayment         (new card)
                    //   * selectedStorageSafeGuid  (saved card; carried in the
                    //                               field below — nothing here)
                    ...(giftCoversAll
                        ? {} // the gift card pays it all — no card is sent or charged
                        : walletOverride
                        ? {
                              walletPayment: {
                                  walletName: walletOverride.walletName,
                                  data: walletOverride.data,
                                  keyId: walletOverride.keyId,
                                  ...(walletOverride.bin ? { bin: walletOverride.bin } : {}),
                                  ...(walletOverride.cardSecurity
                                      ? { cardSecurity: walletOverride.cardSecurity }
                                      : {}),
                              },
                          }
                        : selectedProfile
                        ? {} // saved-card path — server reads selectedStorageSafeGuid
                        : extracted
                            ? { encryptedPayment: extracted.encrypted }
                            : {}),
                    // EFFECTIVE, so the wallet's address reaches the processor
                    // as `Account.Postal` — that is what earns AVS and the
                    // Level-2 billing tier. A live TRX wallet sale without it
                    // returned `avsresp: "N"` (retref F75VEW30FK5Q6R9).
                    billingAddress: effectiveBilling || {
                        address: '',
                        city: '',
                        region: '',
                        postal: '',
                        country: 'US',
                    },

                    // Optional payment features
                    savePayment: savePayment && enableSavePayment,
                    // Saved-card reuse routes to the issuing provider's field
                    // (TRX StorageSafe.Guid).
                    selectedStorageSafeGuid:
                        !giftCoversAll && selectedProfile && selectedSavedProvider === 'trx'
                            ? selectedProfile
                            : undefined,
                    selectedAccountGuid:
                        !giftCoversAll && selectedProfile && selectedSavedProvider === 'trx' && selectedAccountGuid
                            ? selectedAccountGuid
                            : undefined,

                    // Order configuration
                    tipAmount: tipAmount,
                    customerNotes: customerNotes.trim() || undefined,

                    // Price validation — the server re-checks chargeAmount and the
                    // delivery fee against these (amount-lock). The Drive quote is
                    // re-derived server-side at checkout, so the client does NOT
                    // send a quote id.
                    expectedTotal: preview.total,
                    expectedDeliveryFee: preview.deliveryFee,

                    // Transactional SMS opt-in (order confirmation + tracking).
                    smsConsent: smsOrderUpdates,

                    // PROMOTIONAL SMS opt-in — a separate, affirmative consent
                    // (carriers and the TCPA treat marketing as distinct from
                    // transactional; agreeing to order updates is not agreeing
                    // to offers). Sent ONLY when ticked: omitting the field
                    // leaves any opt-in captured elsewhere (e.g. at the POS)
                    // untouched, whereas sending `false` would revoke it.
                    ...(promoText ? { promoText: true } : {}),

                    // Customer Loyalty v1 (B8). The REDEMPTION INTENT (integer
                    // points) — the server re-derives + clamps against a freshly
                    // read balance using the SAME formula /preview used, then
                    // burns post-charge. The UI never sends a dollar amount.
                    redeemPoints: redeemPoints > 0 ? redeemPoints : undefined,

                    // Coupon / promo-code intent — sent ONLY when the code
                    // actually applied at preview (`preview.couponDiscount`
                    // present), so `expectedTotal` reflects it. The server
                    // re-derives + re-validates and fails closed (409
                    // COUPON_INVALID) if it lapsed since preview. A code that only
                    // ERRORED at preview is never sent (customer would be charged
                    // full price — the error line tells them to fix/remove it).
                    ...(discountCode && preview.couponDiscount
                        ? { discountCode }
                        : {}),

                    // Customer Loyalty v1 — ENROLLMENT OPT-IN. Earning is opt-in:
                    // only when the customer ticked "Join rewards" do we ask the
                    // server to enroll the resolved loyalty identity (set the
                    // Firestore earn-gate flag + record the `loyalty` consent
                    // receipt) so this order earns. Sent only when true (and only
                    // when the opt-in was actually shown) — a non-enrolled
                    // identified customer earns nothing. Spread (not a literal
                    // key) so the field threads through without widening the
                    // shared ICheckoutRequest model.
                    ...(showLoyaltyOptIn && joinLoyalty ? { enrollLoyalty: true } : {}),

                    // Gift card tender — the HOLD (never a number) and the gift
                    // leg the guest was shown, the gift half of the amount lock.
                    ...(giftCard
                        ? { giftCardHoldId: giftCard.holdId, expectedGiftCents: giftCents }
                        : {}),

                    // Ad attribution carried from the origin page; the server
                    // forces marketingAllowed=false on a Sec-GPC request.
                    ...(attribution ? { attribution } : {}),

                    // Idempotency
                    idempotencyKey,
                },
                idempotencyKey,
            });

            clearCheckoutAttribution(locationSlug);

            logger.info('Checkout successful', {
                orderGuid: response.orderGuid,
                orderNumber: response.orderNumber,
                transactionRef: response.transactionRef,
            });

            // payment_captured + order_placed — the charge landed and the order
            // was created. `amount` is a string in the response; coerce to number.
            const capturedAmount =
                typeof response.amount === 'string' ? parseFloat(response.amount) : (response.amount ?? preview.total);
            analytics.trackEvent(paymentEvents.paymentCaptured({
                tenantId: String(tenant.tenantId),
                restaurantId: String(locationRecord.restaurantId),
                orderId: String(response.orderGuid),
                transactionId: response.transactionRef ?? '',
                amount: Number.isFinite(capturedAmount) ? capturedAmount : preview.total,
                method: 'card_not_present',
                processor: paymentProvider ?? 'unknown',
                latencyMs: Date.now() - paymentStartedAt,
            }));
            analytics.trackEvent(orderingEvents.orderPlaced({
                orderId: String(response.orderGuid),
                restaurantId: String(locationRecord.restaurantId),
                tenantId: String(tenant.tenantId),
                orderType: cart.orderType ?? 'pickup',
                itemCount: cart.items?.length ?? 0,
                subtotal: preview.subtotal,
                tax: preview.tax,
                tip: tipAmount,
                total: preview.total,
                paymentMethod: walletOverride
                    ? `wallet_${walletOverride.walletName === 'ApplePay' ? 'apple' : 'google'}`
                    : selectedProfile ? 'saved_card' : 'card',
                isFirstOrder: false,
            }));

            // Success! Set state for success UI
            setPaymentSuccess(true);
            setPaymentError(null);

            // Store response for test mode display
            setLastPaymentResponse({
                respstat: 'A',
                amount: response.amount,
                retref: response.transactionRef,
                authcode: 'APPROVED', // Server-side checkout abstracts auth code
                profileid: response.profileId,
                acctid: response.accountId,
            } as IAuthResponse);

            // Saved-card persistence is handled SERVER-SIDE: when a verified-phone
            // customer opts to save, /checkout records the card on-file against
            // the customer (see orders.ts persistSavedCard) and it appears under
            // the loyalty account's "Saved payment methods". The browser never
            // writes payment data. Nothing to persist client-side here.
            if (response.profileId) {
                logger.debug(
                    selectedProfile ? 'Used existing saved card' : 'Card saved on file',
                    { profileId: response.profileId },
                );
            }

            // This cart is spent — retire the browser's copy.
            // The server stamps `convertedAt` and refuses to re-check-out or
            // overwrite a converted cart, but WITHOUT this the stale local cart
            // survives and gets re-offered (and re-synced) to the customer.
            // That resurrection is what let prod re-open an already-paid cart,
            // reproduce its idempotency signature, and replay a voided payment
            // as a success. Best-effort: never let cleanup break the
            // confirmation navigation on a completed order.
            try {
                CartStorageService.clearCart();
                resetIdempotencyKey('checkout');
                resetIdempotencyKey('tip');
            } catch (cleanupError) {
                logger.warn('Post-checkout cart cleanup failed (non-fatal)', {
                    error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
                });
            }

            // Navigate to order confirmation page.
            logger.debug('Navigating to confirmation page');
            // The durable order-access token goes in the URL query (survives a
            // refresh / bookmark / different device) so the confirmation page can
            // re-authorize the order fetch even after the anonymous session is
            // gone. Also passed via route state for the immediate render.
            const confirmationQuery = response.accessToken
                ? `?t=${encodeURIComponent(response.accessToken)}`
                : '';
            navigate(`/${locationSlug}/confirmation/${response.orderGuid}${confirmationQuery}`, {
                state: {
                    transactionRef: response.transactionRef,
                    amount: response.amount,
                    paidAt: response.createdAt,
                    accessToken: response.accessToken,
                },
            });

        } catch (error) {
            logger.error('Checkout failed', error);

            // Rotate the idempotency key ONLY when the charge definitively did
            // not land, so the next submit is a fresh attempt. On an ambiguous
            // failure (network/timeout, 409 in-flight, 5xx) we keep the key so a
            // resubmit is deduped — never a double charge.
            if (isDefinitiveChargeFailure(error)) {
                resetIdempotencyKey('checkout');
            }

            // Translate rather than echo. `translateError` prefers the
            // server's cardholder decline guidance (`error.details.decline`,
            // set by PaymentDeclinedError) over the generic catalog entry, and
            // falls back to catalogued copy for everything else. Echoing
            // `error.message` put the gateway's raw string ("Do not honor") in
            // front of a shopper.
            const translated = translateError(error);
            const errorMessage = translated.message
                || 'Payment failed. Please check your payment details and try again.';

            // payment_failed + order_failed — the charge/order did not complete.
            // The server attaches an HTTP `status`; surface it as the error code
            // when present, else the error name. `isRetry` mirrors whether the
            // idempotency key was preserved (ambiguous failure → a resubmit is a
            // retry of the same intent).
            const errorCode = String(
                (error as { status?: number; code?: string } | null)?.status ??
                (error as { code?: string } | null)?.code ??
                'unknown',
            );
            analytics.trackEvent(paymentEvents.paymentFailed({
                tenantId: String(tenant.tenantId),
                restaurantId: String(locationRecord.restaurantId),
                orderId: String(cart.cartId),
                amount: preview?.total ?? 0,
                method: 'card_not_present',
                processor: paymentProvider ?? 'unknown',
                errorCode,
                errorMessage,
                isRetry: !isDefinitiveChargeFailure(error),
            }));
            analytics.trackEvent(orderingEvents.orderFailed({
                restaurantId: String(locationRecord.restaurantId),
                tenantId: String(tenant.tenantId),
                errorCode,
                errorMessage,
                orderType: cart.orderType ?? 'pickup',
                subtotal: preview?.subtotal ?? 0,
            }));

            const errorCodeName = (error as { code?: string } | null)?.code;
            const scheduledNotice = scheduledCartNoticeFrom(error);
            if (scheduledNotice) {
                // Nothing was charged. Every line, old and new price, and the
                // way back to the menu where the cart is updated on the
                // guest's confirmation.
                setUnavailableItems(null);
                setPaymentErrorTitle(null);
                setPaymentErrorSteps([]);
                setPaymentError(null);
                setSubmitScheduledNotice(scheduledNotice);
            } else if (
                giftCard &&
                (String(errorCodeName ?? '').startsWith('GIFT_CARD') ||
                    errorCodeName === 'PAYMENT_FAILED' ||
                    errorCodeName === 'PARTIAL_APPROVAL')
            ) {
                // The server released (or no longer honours) the gift card
                // hold: clear it so the guest re-applies the card, and say why.
                setGiftCard(null);
                setGiftCardNotice(
                    String(errorCodeName ?? '').startsWith('GIFT_CARD')
                        ? errorMessage
                        : 'Your gift card was released because the card payment did not go through. Apply it again to use it.',
                );
                setUnavailableItems(null);
                setPaymentErrorTitle(translated.title || null);
                setPaymentErrorSteps([...(translated.nextSteps ?? [])]);
                setPaymentError(errorMessage);
            } else if (errorCodeName === 'ITEM_UNAVAILABLE') {
                const listed = (error as { details?: { unavailableItems?: Array<{ name: string; reason: string }> } } | null)
                    ?.details?.unavailableItems;
                setUnavailableItems(Array.isArray(listed) ? listed : []);
                setPaymentErrorTitle(null);
                setPaymentErrorSteps([]);
                setPaymentError(errorMessage);
            // Check if this is a price change error
            // Matched on the CODE. This used to match the translated message's
            // text, which the translator is free to reword — and an
            // uncatalogued PRICE_CHANGED rendered generic copy that never
            // contained "changed", so the preview was never refreshed.
            } else if (translated.code === 'PRICE_CHANGED') {
                setUnavailableItems(null);
                // Price changed since preview - refresh preview and ask user to review
                logger.info('Price changed, refreshing preview');
                await refetchPreview();
                setPaymentErrorTitle(null);
                setPaymentErrorSteps([]);
                setPaymentError('The order total has changed. Please review the updated pricing and try again.');
            } else {
                setUnavailableItems(null);
                setPaymentErrorTitle(translated.title || null);
                setPaymentErrorSteps([...(translated.nextSteps ?? [])]);
                setPaymentError(errorMessage);
            }

            setPaymentSuccess(false);

            // Store failed response for test mode display
            setLastPaymentResponse({
                respstat: 'D',
                resptext: errorMessage,
            } as IAuthResponse);
        }
    };

    // Handle tip adjustment (capture with tip added to original amount)
    const handleTipAdjustment = async () => {
        if (!lastPaymentResponse?.retref || !tipAdjustmentAmount) {
            return;
        }

        const tip = parseFloat(tipAdjustmentAmount);
        if (isNaN(tip) || tip <= 0) {
            setPaymentError('Invalid tip amount');
            return;
        }

        try {
            const originalAmount = parseFloat(lastPaymentResponse.amount || '0');
            const newTotal = originalAmount + tip;

            // Idempotency key — minted once per tip-capture intent (retref +
            // new total) and held across retries; rotated on a definitive
            // failure below so a corrected retry isn't replayed as the cached
            // result. Required by the strict-idempotency capture endpoint.
            const idempotencyKey = resolveIdempotencyKey(
                'tip',
                `${lastPaymentResponse.retref}:${newTotal}`,
                lastPaymentResponse.retref,
            );

            logger.info('Processing tip adjustment', {
                retref: lastPaymentResponse.retref,
                originalAmount,
                tipAmount: tip,
                newTotal,
                idempotencyKey,
            });

            const captureRequest: ICaptureRequest = {
                retref: lastPaymentResponse.retref,
                amount: formatAmountForAPI(newTotal),
                idempotencyKey, // Required for strict idempotency

                // Provider routing — the neutral /capture route resolves the
                // processor from these (400s without them).
                tenantId: tenant?.tenantId,
                restaurantId: locationRecord?.restaurantId,

                // Response options
                receipt: 'json', // Request receipt data in response
                bin: 'Y', // Request BIN (card type) data in response

                // Transaction context (match initial auth)
                ecomind: 'E', // E-commerce indicator
                cof: 'C', // Customer-Initiated Transaction
                cofscheduled: 'N', // One-time payment (not scheduled/recurring)
            };

            const captureResponse = await captureMutation.mutateAsync(captureRequest);

            logger.debug('Tip adjustment response', { respstat: captureResponse.respstat });

            if (captureResponse.respstat === 'A') {
                setTipAdjustmentSuccess(true);
                setLastPaymentResponse(captureResponse);
                setTipAdjustmentAmount('');
                setPaymentError(null);
                logger.info('Tip adjustment successful', { amount: captureResponse.amount });
            } else {
                // Definitive capture decline — rotate so a corrected retry is a
                // fresh attempt, not a replay of this declined capture.
                resetIdempotencyKey('tip');
                // The capture route attaches cardholder-safe guidance to every
                // non-approved response. Prefer it over `resptext`, which is
                // the gateway's own operator vocabulary ("Do not honor") and
                // was going straight to a shopper here.
                const declined = captureResponse.decline;
                setPaymentErrorTitle(declined?.title ?? null);
                setPaymentErrorSteps([...(declined?.nextSteps ?? [])]);
                setPaymentError(
                    declined?.message
                        ?? captureResponse.resptext
                        ?? 'Tip adjustment failed',
                );
                logger.error('Tip adjustment failed', {
                    respcode: captureResponse.respcode,
                    declineKey: declined?.key,
                });
            }
        } catch (error) {
            logger.error('Tip adjustment error', error);
            if (isDefinitiveChargeFailure(error)) {
                resetIdempotencyKey('tip');
            }
            setPaymentError(error instanceof Error ? error.message : 'Tip adjustment failed. Please try again.');
        }
    };

    /**
     * The order-summary controls and the tip picker, each defined ONCE.
     *
     * The mobile card and the desktop sticky rail put these in genuinely
     * different containers, so this is not a CSS problem — but the PROPS were
     * byte-identical duplicates, which meant every change had to be made twice
     * or the two silently drifted apart. `pickupDetailsProps` above already
     * establishes the idiom for this file; these extend it to the rest.
     */
    const renderSummaryControls = () => (
        <>
            <OrderSummary
                subtotal={subtotal}
                taxAndFees={taxAndFees}
                tipAmount={tipAmount}
                deliveryFee={deliveryFee}
                total={total}
                discountAmount={(preview?.loyaltyDiscount?.cents ?? 0) / 100}
                couponAmount={(preview?.couponDiscount?.cents ?? 0) / 100}
                couponCode={preview?.couponDiscount?.code}
                cartItems={cart?.items || []}
                pending={totalsPending}
                onlineServiceFee={onlineServiceFee}
                locationName={locationDisplayName}
            />
            {/* Customer Loyalty v1 — redemption (self-hides unless the flag is
                on + the customer has a positive balance). */}
            <LoyaltyRedeem
                tenantDocId={tenantDocId}
                restaurantDocId={locationDocId}
                customerUid={customerUid}
                cartId={cart?.cartId ?? null}
                tipAmount={initialTipAmount}
                redeemPoints={redeemPoints}
                onRedeemPointsChange={setRedeemPoints}
                loyaltyDiscount={preview?.loyaltyDiscount}
                loyaltyEnabled={loyaltyFlagEnabled}
            />
            {/* Promo / coupon code — server-validated. */}
            <PromoCode
                appliedCode={discountCode || undefined}
                couponDiscount={preview?.couponDiscount}
                couponError={preview?.couponError}
                pending={isPreviewStale}
                onApply={setDiscountCode}
                onRemove={() => setDiscountCode('')}
            />
            {/* Gift card tender — self-hides unless the restaurant offers gift
                cards online (dark behind initiative.gift_cards_online). */}
            <GiftCardTender
                slug={locationSlug}
                cartId={cart?.cartId ?? null}
                totalCents={totalsPending || !preview ? null : totalCents}
                applied={giftCard}
                onChange={(applied) => {
                    setGiftCard(applied);
                    if (applied) setGiftCardNotice(null);
                }}
                notice={giftCardNotice}
            />
        </>
    );

    const renderTipSelection = () => (
        <TipSelection
            subtotal={subtotal}
            tipCentsFor={tipCentsFor}
            selectedTip={selectedTip}
            customTip={customTip}
            onTipChange={(percentage) => {
                setSelectedTip(percentage);
                setCustomTip('');
            }}
            onCustomTipChange={(amountInDollars) => {
                setSelectedTip(0);
                setCustomTip(String(amountInDollars));
            }}
        />
    );

    // Define loading state - we'll show skeletons inline instead of full-page loading.
    // The trailing `(!cart && !cartError)` keeps the skeleton up during the brief
    // window after the restaurant resolves but before the (now-enabled) cart query
    // has started fetching — without it a disabled→enabled tick would render the
    // summary against an undefined cart and flash empty/zeroed values.
    const isDataLoading = isLocationLoading || isCartLoading || (!cart && !cartError);

    // Show error state - Restaurant not found
    if (!isLocationLoading && !locationRecord) {
        return (
            <div className="min-h-svh bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Restaurant Not Found</h1>
                    <p className="text-gray-600 mb-4">
                        We couldn't find a restaurant with the slug:{' '}
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">{locationSlug}</code>
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        URL format: <code className="bg-gray-100 px-2 py-1 rounded text-sm">/restaurant-slug?cart=cart_123</code>
                    </p>
                    {locationError && (
                        <details className="text-left bg-red-50 p-4 rounded-lg">
                            <summary className="cursor-pointer text-red-700 font-medium">Error Details</summary>
                            <pre className="mt-2 text-xs text-red-600 overflow-auto">
                                {JSON.stringify(locationError, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }

    // Show error state - Cart not found or missing cart GUID.
    // Only declare the cart "not found" once the query has actually SETTLED
    // (fetched or errored) and returned nothing. A disabled/loading query (e.g.
    // while the restaurant is still resolving, so tenantId/restaurantId aren't
    // known yet) reports isLoading=false in TanStack Query v5 — the old
    // `!isCartLoading && !cart` guard fired in that window and flashed a bogus
    // "Cart Not Found" before the cart had even begun loading.
    if (!cartGuid || ((isCartFetched || !!cartError) && !cart)) {
        return (
            <div className="min-h-svh bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">
                        {!cartGuid ? 'Cart Missing' : 'Cart Not Found'}
                    </h1>
                    <p className="text-gray-600 mb-4">
                        {!cartGuid
                            ? 'No cart was provided in the URL.'
                            : 'We couldn\'t find your cart. It may have expired or been deleted.'}
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        Please return to the menu and try again.
                    </p>
                    <a
                        href={menuUrl}
                        className="inline-block text-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Back to Menu
                    </a>
                    {cartError && (
                        <details className="text-left bg-red-50 p-4 rounded-lg mt-4">
                            <summary className="cursor-pointer text-red-700 font-medium">Error Details</summary>
                            <pre className="mt-2 text-xs text-red-600 overflow-auto">
                                {JSON.stringify(cartError, null, 2)}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }

    return (
        // Checkout renders the ordering app's own `ScheduleModal` and
        // `DeliveryAddressDialog`, which read the brand as `--menu-primary`.
        // Without this scope they would fall back to the platform default, so
        // the customer's brand would change at the hand-off. Each token also
        // carries a CSS fallback, so the scope is what makes it CORRECT rather
        // than what stops it breaking.
        <MenuThemeScope restaurant={locationRecord} className="min-h-svh bg-gray-50">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row lg:gap-8">
                    {/* Main Content - Left Side */}
                    <div className="flex-1 bg-white lg:bg-transparent">
                        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
                            {/* Back Button */}
                            <a
                                href={menuUrl}
                                className="flex w-fit items-center gap-2 text-gray-700 hover:text-gray-900 mb-6 transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                                <span className="font-medium">Menu</span>
                            </a>

                            {/* Title */}
                            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Checkout</h1>

                            {/* DEV ONLY: Test Amount Override for processor validation */}
                            {showDebugUI && (
                                <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                                    <h3 className="text-sm font-semibold text-yellow-900 mb-2">
                                        🧪 Processor Validation Test Mode
                                    </h3>

                                    {/* Test Amount */}
                                    <div className="mb-4">
                                        <p className="text-xs text-yellow-800 mb-2">
                                            <strong>Test Amount:</strong> Enter exact amount to test (e.g., 9.32 or 1100.35). Bypasses cart calculation.
                                        </p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                placeholder="e.g., 55.25"
                                                value={testAmount}
                                                onChange={(e) => setTestAmount(e.target.value)}
                                                className="flex-1 px-3 py-2 border border-yellow-400 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                            />
                                            <button
                                                onClick={() => setTestAmount('')}
                                                className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                                            >
                                                Clear
                                            </button>
                                        </div>
                                        {isTestMode && (
                                            <p className="mt-2 text-xs font-medium text-green-700">
                                                ✅ Test Mode Active: ${parseFloat(testAmount).toFixed(2)} (no tax/tip)
                                            </p>
                                        )}
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            <span className="text-xs text-yellow-800 font-medium">Quick presets:</span>
                                            <button
                                                onClick={() => setTestAmount('55.25')}
                                                className="px-2 py-1 bg-white border border-yellow-400 text-yellow-900 text-xs rounded hover:bg-yellow-100 transition-colors"
                                            >
                                                $55.25
                                            </button>
                                            <button
                                                onClick={() => setTestAmount('100.25')}
                                                className="px-2 py-1 bg-white border border-yellow-400 text-yellow-900 text-xs rounded hover:bg-yellow-100 transition-colors"
                                            >
                                                $100.25
                                            </button>
                                            <button
                                                onClick={() => setTestAmount('1100.35')}
                                                className="px-2 py-1 bg-white border border-yellow-400 text-yellow-900 text-xs rounded hover:bg-yellow-100 transition-colors"
                                            >
                                                $1,100.35 (Decline)
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Success Banner - DEBUG ONLY */}
                            {showDebugUI && paymentSuccess && lastPaymentResponse && (
                                <div className="mb-6 p-6 bg-green-50 border-2 border-green-500 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0">
                                            <svg
                                                className="h-8 w-8 text-green-600"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-green-900 mb-2">
                                                {tipAdjustmentSuccess ? '✅ Tip Adjustment Successful!' : '✅ Payment Successful!'}
                                            </h3>
                                            <div className="space-y-1.5 text-sm text-green-800">
                                                <p>
                                                    <strong>Amount:</strong> ${lastPaymentResponse.amount}
                                                </p>
                                                <p>
                                                    <strong>Authorization Code:</strong> {lastPaymentResponse.authcode || 'N/A'}
                                                </p>
                                                <p>
                                                    <strong>Transaction Reference:</strong> {lastPaymentResponse.retref}
                                                </p>
                                                {lastPaymentResponse.token && (
                                                    <p>
                                                        <strong>Token:</strong> {lastPaymentResponse.token}
                                                    </p>
                                                )}
                                                {lastPaymentResponse.profileid && (
                                                    <p>
                                                        <strong>Profile ID:</strong> {lastPaymentResponse.profileid}/
                                                        {lastPaymentResponse.acctid}
                                                    </p>
                                                )}
                                                <p className="pt-2 text-xs">
                                                    Your order has been successfully processed. Keep the transaction
                                                    reference for your records.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tip Adjustment Section - DEBUG ONLY */}
                            {showDebugUI && paymentSuccess && lastPaymentResponse && !accountVerificationMode && !tipAdjustmentSuccess && (
                                <div className="mb-6 p-6 bg-blue-50 border-2 border-blue-400 rounded-lg">
                                    <h3 className="text-lg font-bold text-blue-900 mb-3">💡 Add Tip</h3>
                                    <p className="text-sm text-blue-800 mb-4">
                                        Would you like to add a tip to this transaction?
                                    </p>
                                    <div className="flex gap-3">
                                        <div className="flex-1">
                                            <label className="block text-sm font-medium text-blue-900 mb-2">
                                                Tip Amount ($)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tipAdjustmentAmount}
                                                onChange={(e) => setTipAdjustmentAmount(e.target.value)}
                                                placeholder="10.00"
                                                className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                            {tipAdjustmentAmount && (
                                                <p className="text-xs text-blue-700 mt-2">
                                                    New total: ${(parseFloat(lastPaymentResponse.amount || '0') + parseFloat(tipAdjustmentAmount || '0')).toFixed(2)}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                onClick={handleTipAdjustment}
                                                disabled={!tipAdjustmentAmount || captureMutation.isPending}
                                                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {captureMutation.isPending ? (
                                                    <span className="motion-safe:animate-pulse">Processing…</span>
                                                ) : (
                                                    'Add Tip'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-3">
                                        <strong>Note:</strong> This will update the transaction amount to include your tip.
                                    </p>
                                </div>
                            )}

                            {/* DEV ONLY: Display Last Payment Response */}
                            {showDebugUI && lastPaymentResponse && (
                                <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
                                    <h3 className="text-sm font-semibold text-blue-900 mb-2">
                                        📋 Last Payment Response
                                    </h3>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-blue-800">
                                                <strong>Status:</strong>{' '}
                                                <span
                                                    className={
                                                        lastPaymentResponse.respstat === 'A'
                                                            ? 'text-green-700 font-bold'
                                                            : 'text-red-700 font-bold'
                                                    }
                                                >
                                                    {lastPaymentResponse.respstat === 'A' ? '✅ Approved' : '❌ Declined'}
                                                </span>
                                            </span>
                                            <span className="text-xs text-blue-800">
                                                <strong>Amount:</strong> ${lastPaymentResponse.amount}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex-1">
                                                <p className="text-xs text-blue-800 mb-1">
                                                    <strong>Retref (processor reference):</strong>
                                                </p>
                                                <code className="block px-2 py-1 bg-white border border-blue-300 rounded text-xs font-mono text-blue-900">
                                                    {lastPaymentResponse.retref || 'N/A'}
                                                </code>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    if (lastPaymentResponse.retref) {
                                                        navigator.clipboard.writeText(lastPaymentResponse.retref);
                                                        alert('Retref copied to clipboard!');
                                                    }
                                                }}
                                                className="px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                        <p className="text-xs text-blue-800">
                                            <strong>Response:</strong> {lastPaymentResponse.resptext || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Mobile/Tablet - All sections in one container */}
                            {isDataLoading ? (
                                <MobileOrderSummarySkeleton />
                            ) : (
                                <div className="lg:hidden mb-8">
                                    <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                                        <PickupDetails layout="stack" {...pickupDetailsProps} />
                                        {renderSummaryControls()}
                                        {renderTipSelection()}
                                    </div>
                                </div>
                            )}

                            {/* Desktop - Pickup Details. The phone summary card
                                above already shows these rows, so this instance
                                (and its skeleton) is desktop-only. */}
                            <div className="hidden lg:block">
                                {isDataLoading ? (
                                    <PickupDetailsSkeleton />
                                ) : (
                                    <PickupDetails layout="section" {...pickupDetailsProps} />
                                )}
                            </div>

                            {/* Desktop Tip Section - Hidden on mobile/tablet.
                                Skeletoned until the cart loads so the tip pills
                                never paint "$0.00" off a zeroed subtotal. Once the
                                cart is in, the pills read the real subtotal-based
                                amounts immediately and stay stable across a
                                tip/redeem recalc (keepPreviousData on the preview). */}
                            {isDataLoading ? (
                                <div className="mb-8 hidden lg:block" aria-hidden="true">
                                    <div className="h-6 w-16 rounded bg-gray-200 motion-safe:animate-pulse mb-4" />
                                    <div className="flex gap-3">
                                        <div className="h-16 flex-1 rounded-xl bg-gray-200 motion-safe:animate-pulse" />
                                        <div className="h-16 flex-1 rounded-xl bg-gray-200 motion-safe:animate-pulse" />
                                        <div className="h-16 flex-1 rounded-xl bg-gray-200 motion-safe:animate-pulse" />
                                        <div className="h-16 flex-1 rounded-xl bg-gray-200 motion-safe:animate-pulse" />
                                    </div>
                                </div>
                            ) : (
                            <div className="hidden lg:block">
                                {renderTipSelection()}
                            </div>
                            )}

                            {/* Customer Information */}
                            <CustomerInformation
                                restaurantName={locationDisplayName}
                                customerInfo={customerInfo}
                                onCustomerInfoChange={setCustomerInfo}
                                promoEmail={promoEmail}
                                promoText={promoText}
                                onPromoEmailChange={setPromoEmail}
                                onPromoTextChange={setPromoText}
                                smsOrderUpdates={smsOrderUpdates}
                                onSmsOrderUpdatesChange={setSmsOrderUpdates}
                                showLoyaltyOptIn={showLoyaltyOptIn}
                                loyaltyProgramName={loyaltyProgram?.displayName}
                                joinLoyalty={joinLoyalty}
                                onJoinLoyaltyChange={setJoinLoyalty}
                            />

                            {/* Special instructions — persisted as the order's
                                customerNotes and shown to the kitchen on the POS
                                order hub. */}
                            <div className="mb-8">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                    Special instructions
                                </h2>
                                <label
                                    htmlFor="customer-notes"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Allergies or notes for the kitchen (optional)
                                </label>
                                <textarea
                                    id="customer-notes"
                                    value={customerNotes}
                                    onChange={(e) => setCustomerNotes(e.target.value)}
                                    maxLength={500}
                                    rows={3}
                                    placeholder="e.g. peanut allergy, no onions, ring doorbell"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder-gray-400 resize-none"
                                />
                            </div>

                            {/* Billing Address - AVS */}
                            <BillingAddress
                                onAddressChange={setBillingAddress}
                                customerName={`${customerInfo.firstName} ${customerInfo.lastName}`.trim()}
                                customerPhone={customerInfo.phone}
                                customerEmail={customerInfo.email}
                            />

                            {/* Payment — provider-aware wrapper.
                                * trx → TRX JS library (per-field iframes; encrypted payload extracted on submit).
                                  Submit is gated on `trxFieldsReady` (the
                                  library's onReady signal). */}
                            {/* Saved-card picker — verified-phone accounts with
                                cards on file at this restaurant. Selecting one
                                charges it as a stored credential; "Use a new
                                card" reveals the input below. */}
                            {hasVerifiedPhone && savedCards.length > 0 && (
                                <div className="mb-6">
                                    <p className="mb-2 text-sm font-medium text-gray-900">
                                        Your saved cards
                                    </p>
                                    <div className="space-y-2">
                                        {savedCards.map((card) => {
                                            const isSel =
                                                selectedProfile === card.instrument.providerTokenId &&
                                                selectedSavedProvider === card.instrument.provider;
                                            return (
                                                <button
                                                    type="button"
                                                    key={card.methodId}
                                                    onClick={() => {
                                                        setSelectedProfile(card.instrument.providerTokenId);
                                                        setSelectedSavedProvider(card.instrument.provider);
                                                        setSelectedAccountGuid(
                                                            card.instrument.providerNestedTokenId ?? null,
                                                        );
                                                        setPaymentError(null);
                                                    }}
                                                    className={`flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                                                        isSel
                                                            ? 'border-gray-900 bg-gray-50'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                >
                                                    <span className="text-sm text-gray-900">
                                                        {card.instrument.brandLabel ?? 'Card'} ••••{' '}
                                                        {card.instrument.lastFour ?? '????'}
                                                        {card.isDefault && (
                                                            <span className="ml-2 text-xs text-gray-500">
                                                                Default
                                                            </span>
                                                        )}
                                                    </span>
                                                    {isSel && (
                                                        <span className="text-xs font-medium text-gray-900">
                                                            Selected
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedProfile(null);
                                                setSelectedSavedProvider(null);
                                                setSelectedAccountGuid(null);
                                            }}
                                            className={`flex w-full items-center rounded-xl border-2 px-4 py-3 text-left text-sm transition-colors ${
                                                !selectedProfile
                                                    ? 'border-gray-900 bg-gray-50 text-gray-900'
                                                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                        >
                                            Use a new card
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* TRX waits for `preview` before mounting: the PayPage
                                session's innerAction is chosen from whether an amount
                                is present, and `preview` (the total) lands a couple of
                                async hops after `paymentProvider`. Mounting pre-preview
                                minted a throwaway (no-amount) session + PayPage init,
                                then re-minted the instant the total arrived — a double
                                gateway round-trip + a field-iframe re-init on every
                                load. Gating on `preview` mints the session exactly once,
                                with the amount already known. (The innerAction itself —
                                Sale for immediate-capture tip modes vs Auth for the
                                afterAuth/pre-auth path — is the mint's concern, not this
                                gate's.) */}
                            {giftCoversAll ? (
                                // The gift card pays the whole order: no card form.
                                <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                                    Your gift card covers this order — no card needed.
                                </div>
                            ) : paymentProvider && !selectedProfile && !!preview ? (
                                <PaymentInput
                                    ref={paymentRef}
                                    provider={paymentProvider}
                                    tenantId={tenant?.tenantId ?? 0}
                                    restaurantId={locationRecord?.restaurantId ?? 0}
                                    paymentType="card"
                                    amount={preview ? {
                                        // The card is charged only what the gift card does not cover.
                                        minorUnits: giftCard ? cardDueCents : Math.round(preview.total * 100),
                                        currency: 'USD',
                                    } : undefined}
                                    onValidationError={(error) => {
                                        logger.warn('Validation error', { error });
                                    }}
                                    onReady={setTrxFieldsReady}
                                    // TRX restaurants get their wallets from here and
                                    // nowhere else — removing this took Apple/Google
                                    // Pay off every TRX restaurant in production once.
                                    // TRX's library supplies the gateway config itself.
                                    //
                                    // The trade is real: TRX's wallet returns only the
                                    // encrypted token, so a TRX wallet payer still
                                    // types their billing address.
                                    enableWallets={resolveWalletOwner(paymentProvider) === 'trx-library'}
                                    onWalletResult={(result) => {
                                        // Wallet sheet completed — submit immediately
                                        // (~1-min cryptogram TTL).
                                        void handlePayment(result);
                                    }}
                                />
                            ) : !paymentProvider && locationRecord ? (
                                // Restaurant loaded but no (known) processor configured —
                                // fail loud, never render a guessed form. (When a saved
                                // card is selected, paymentProvider IS set, so this notice
                                // does not show — the picker above carries the payment.)
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                    Online payments aren&apos;t available for this restaurant yet.
                                    Please contact the restaurant to complete your order.
                                </div>
                            ) : null /* restaurant still loading, or a saved card is selected */}

                            {/* Save Payment Method (feature-flag controlled).
                                Provider-agnostic — the server vault enrolls on
                                the routed processor (TRX StorageSafe). Shown only to verified-phone
                                accounts (guests can pay but never save a card),
                                matching the server `requirePhoneVerified` gate. */}
                            {paymentProvider &&
                                hasVerifiedPhone && !selectedProfile && (
                                <SavePaymentMethod
                                    checked={savePayment}
                                    onChange={setSavePayment}
                                    enabled={enableSavePayment}
                                    // The $0-account-verification ("save card without
                                    // purchase") sub-toggle is intentionally NOT wired:
                                    // the server checkout API rejects that mode, so
                                    // exposing it produced a hard error for users. Re-enable
                                    // by passing verificationMode/onVerificationModeChange
                                    // once the server-side $0 auth path is implemented.
                                />
                            )}

                            {/* Scheduled menu pricing: the server refused the cart
                                because a price moved with the clock or an item
                                cannot be ordered now — at preview or at submit. */}
                            {scheduledCartNotice && (
                                <ScheduledCartNoticePanel
                                    notice={scheduledCartNotice}
                                    isApplying={scheduledConfirm.isApplying}
                                    // The menu page the guest came from — never
                                    // history.back(), which may not be it.
                                    menuUrl={menuUrl}
                                    onConfirm={() => {
                                        if (!cart || !locationRecord || !tenant) return;
                                        scheduledConfirm.confirm({
                                            cart,
                                            tenantId: tenant.tenantId,
                                            restaurantId: locationRecord.restaurantId,
                                            notice: scheduledCartNotice,
                                        });
                                    }}
                                />
                            )}

                            {/* Preview Error Display */}
                            {previewError && !previewLoading && !previewScheduledNotice && !previewUnavailable && (
                                <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="flex items-start gap-3">
                                        <svg
                                            className="w-6 h-6 text-yellow-600 shrink-0 mt-0.5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="text-sm font-bold text-yellow-900 mb-1">Unable to Calculate Totals</p>
                                            <p className="text-sm text-yellow-800">
                                                We're having trouble calculating your order total. Please refresh the page and try again.
                                            </p>
                                            <button
                                                onClick={() => refetchPreview()}
                                                className="mt-2 text-sm font-medium text-yellow-900 underline hover:text-yellow-700"
                                            >
                                                Retry Calculation
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Payment Error Display */}
                            {shownUnavailableItems && shownUnavailableMessage && (
                                <div
                                    role="alert"
                                    className="mb-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300"
                                >
                                    <p className="text-sm font-bold text-amber-900 mb-1">
                                        Something in your cart is no longer available
                                    </p>
                                    <p className="text-sm text-amber-900 mb-2">{shownUnavailableMessage}</p>
                                    {shownUnavailableItems.length > 0 && (
                                        <ul className="text-sm text-amber-900 mb-3 list-disc pl-5 space-y-0.5">
                                            {shownUnavailableItems.map((item) => (
                                                <li key={`${item.name}-${item.reason}`}>
                                                    <span className="font-medium">{item.name}</span>
                                                    {' — '}
                                                    {item.reason === 'not_available_now'
                                                        ? 'not available at this time'
                                                        : item.reason === 'not_offered_online'
                                                          ? 'not available for online ordering'
                                                          : item.reason === 'size_unavailable'
                                                            ? 'the size you picked is no longer offered'
                                                            : item.reason === 'option_unavailable'
                                                              ? 'an option you picked is sold out'
                                                              : 'sold out'}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <a
                                        href={menuUrl}
                                        className="inline-block px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors"
                                    >
                                        Back to menu to change your order
                                    </a>
                                </div>
                            )}

                            {paymentError && !unavailableItems && (
                                <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="flex items-start gap-3">
                                        <svg
                                            className="w-6 h-6 text-red-600 shrink-0 mt-0.5"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="text-sm font-bold text-red-900 mb-1">
                                                {paymentErrorTitle ?? 'Payment Failed'}
                                            </p>
                                            <p className="text-sm text-red-800 mb-2">{paymentError}</p>
                                            {paymentErrorSteps.length > 0 && (
                                                <ul className="text-sm text-red-800 mb-2 list-disc pl-5 space-y-0.5">
                                                    {paymentErrorSteps.map((step) => (
                                                        <li key={step}>{step}</li>
                                                    ))}
                                                </ul>
                                            )}
                                            {lastPaymentResponse && lastPaymentResponse.respstat !== 'A' && (
                                                <div className="mt-2 pt-2 border-t border-red-200 space-y-1 text-xs text-red-700">
                                                    {lastPaymentResponse.respcode && (
                                                        <p>
                                                            <strong>Response Code:</strong> {lastPaymentResponse.respcode}
                                                        </p>
                                                    )}
                                                    {lastPaymentResponse.retref && (
                                                        <p>
                                                            <strong>Reference:</strong> {lastPaymentResponse.retref}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                            <p className="text-xs text-red-600 mt-2">
                                                Please check your payment details and try again, or use a different
                                                payment method.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Live time correction — a scheduled slot that passed
                                while paying (or a now-closed kitchen) blocks submit
                                with a calm prompt + one-tap recovery, never a
                                dead-end back to the menu. */}
                            {timeBlocksCheckout && timeState.warning && (
                                <div className="mb-3 flex flex-col items-center gap-2 text-center">
                                    <p className="text-xs text-amber-700">{timeState.warning}</p>
                                    <div className="flex items-center gap-2">
                                        {asapAvailable && (
                                            <button
                                                type="button"
                                                onClick={handleSwitchToAsap}
                                                disabled={cartConfigMutation.isPending}
                                                className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-60"
                                            >
                                                {cartConfigMutation.isPending ? 'Updating…' : 'Switch to ASAP'}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setIsScheduleOpen(true)}
                                            disabled={cartConfigMutation.isPending}
                                            className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-50 disabled:opacity-60"
                                        >
                                            Pick a new time
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Card-hold disclosure — shown only when the server will
                                AUTHORIZE this card and capture it at end of day
                                (preview.cardHold). The guest is told before they pay
                                that the final charge can differ from the hold because
                                of changes they ask for. */}
                            {preview?.cardHold && (
                                <p className="mb-3 text-xs leading-relaxed text-gray-500">
                                    Your card will be authorized for this total now and charged when
                                    the restaurant closes out for the day. If you ask the restaurant to
                                    change your order, the final charge will reflect those changes;
                                    anything added will be charged to this same card.
                                </p>
                            )}

                            {/* Place Order Button - Use Restaurant Brand Color */}
                            <button
                                type="button"
                                disabled={!isFormValid || checkoutMutation.isPending || timeBlocksCheckout || isPreviewStale || cartConfigMutation.isPending}
                                onClick={() => void handlePayment()}
                                className="w-full text-white font-semibold py-4 rounded-xl text-base transition-colors flex items-center justify-center gap-2 mb-6 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor:
                                        locationRecord?.websiteConfig?.branding?.colors?.primary || '#DC2626',
                                }}
                            >
                                {checkoutMutation.isPending ? (
                                    // Spinner-free pending state: the label pulses (skeleton idiom)
                                    // while the order is processed — no spinning indicator.
                                    <span className="motion-safe:animate-pulse">Processing payment…</span>
                                ) : (
                                    <>
                                        <span>
                                            {missingContact.length > 0
                                                ? 'Enter your contact info'
                                                : selectedProfile
                                                ? placeOrderLabel
                                                : !trxFieldsReady
                                                    ? 'Loading payment form…'
                                                    : !billingAddress
                                                        ? 'Enter billing address'
                                                        : accountVerificationMode
                                                            ? 'Save payment method • $0.00'
                                                            : placeOrderLabel}
                                        </span>
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                            />
                                        </svg>
                                    </>
                                )}
                            </button>

                            {/* Terms - Using Real Restaurant Name */}
                            <p className="text-xs text-gray-600 text-center mb-4">
                                By signing up, you agree to receive email marketing communications from{' '}
                                {locationDisplayName} and our technology partner OrderUp and consent to our{' '}
                                <a href="#" className="underline hover:text-gray-900">
                                    Terms & Policies
                                </a>
                                . You may receive email or SMS notifications from us for order updates and account
                                access and can opt out any time.
                            </p>

                            {/* Footer */}
                            <div className="border-t border-gray-200 pt-6 space-y-4">
                                <div className="flex items-center justify-center gap-2">
                                    <div className="w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">O</span>
                                    </div>
                                    <span className="text-sm text-gray-600">Powered by OrderUp</span>
                                </div>
                                <div className="flex items-center justify-center gap-6 text-xs text-gray-600">
                                    <a href="#" className="hover:text-gray-900">
                                        Terms & Policies
                                    </a>
                                    <a href="#" className="hover:text-gray-900">
                                        Accessibility Statement
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Order Summary - Right Sidebar (desktop). This column owns the
                        width and stickiness; the summary card, loyalty and promo
                        controls stack inside one sticky rail so none of them floats
                        loose as an extra column in the page flex row. Mobile renders
                        these in the inline section above instead. */}
                    <div className="hidden lg:block lg:w-96 xl:w-md">
                        <div className="sticky top-8 space-y-4">
                            {isDataLoading ? (
                                <OrderSummarySkeleton />
                            ) : (
                                <>
                                    {renderSummaryControls()}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Order-config parity modals — the SAME pickers the ordering app
                uses (shared @order-up/client components), so time and address
                changes behave identically on both sides of the hand-off. */}
            <ScheduleModal
                isOpen={isScheduleOpen}
                onClose={() => setIsScheduleOpen(false)}
                orderType={toScheduledFulfilment(cart?.orderType)}
                onScheduleConfirm={(date) => handleScheduleConfirm(date)}
                selectedDate={cart?.scheduledTime ? new Date(cart.scheduledTime) : undefined}
                selectedTime={cart?.scheduledTime ? formatTime(new Date(cart.scheduledTime)) : 'ASAP'}
                restaurant={locationRecord}
            />
            <DeliveryAddressDialog
                isOpen={isAddressOpen}
                onClose={() => setIsAddressOpen(false)}
                restaurant={locationRecord}
                initialAddress={cart?.deliveryAddressComponents}
                onSave={handleSaveAddress}
            />
        </MenuThemeScope>
    );
}
